import dayjs from "dayjs";
import { useCallback, useEffect, useRef, useState } from "react";
import { getActivityTracker } from "@endpoints/activityTracker/activityTracker.service";
import { EnumActivityTracker } from "@utils/constants";
import { IActivityTracker } from "@endpoints/types/activityTracker.type";
import TrackIcon from "@assets/svg/TrackIcon";
import LoadingAnimation from "@assets/svg/LoadingAnimation";

const BoldText = ({ children }) => {
  return <span className="font-semibold text-gray-500">{children}</span>;
};


function getDescription(type: EnumActivityTracker, data: IActivityTracker) {
  switch (type) {
    case EnumActivityTracker.SUCCESSFUL_PLACEMENT:
      return (
        <span>
          You have been successful in your application for the position titled{" "}
          <span className="text-[#3DCCDE] font-semibold">{data?.jobId?.title}</span>
        </span>
      );
    case EnumActivityTracker.OFFER_RECEIVED:
      return (
        <span className="text-xs gap-1 flex flex-wrap text-light-black">
          <BoldText>{data?.recruiterAgencyId?.companyName} </BoldText>
          sent you an offer from{" "}
          <BoldText>{data?.hiringCompanyId?.companyName} </BoldText>
          regarding
          <BoldText>{data?.jobId?.title}.</BoldText>
        </span>
      );
    case EnumActivityTracker.INTERVIEW_INVITE_SENT:
      return (
        <span className="text-xs gap-1 flex flex-wrap text-light-black">
          <BoldText>ABC Recruitment </BoldText>
          has sent you an <BoldText>interview invitation </BoldText>
          for
          <BoldText>11am Monday 20th October 2025.</BoldText>
        </span>
      );
    case EnumActivityTracker.MEETING_FORWARDED:
      return (
        <span className="text-xs gap-1 flex flex-wrap text-light-black">
          <BoldText>{data?.recruiterAgencyId?.companyName} </BoldText>
          has sent you an <BoldText>availability poll. </BoldText>
        </span>
      );
    case EnumActivityTracker.CANDIDATE_SHORTLISTED:
      return (
        <span className="text-xs gap-1 flex flex-wrap text-light-black">
          <BoldText>Google Inc </BoldText>
          has shortlisted your application for the position
          <BoldText>React Frontend Developer.</BoldText>
        </span>
      );
    case EnumActivityTracker.SHORTLIST_BY_RECRUITER_AGENCY:
      return (
        <span>
          Your application for the position of{" "}
          <span className="text-[#3DCCDE] font-semibold">{data?.jobId?.title}</span> has been
          shortlisted by the hiring company.
        </span>
      );
    case EnumActivityTracker.APPLICATION_SUBMITTED:
      return (
        <span>
          Your application for the position of{" "}
          <span className="text-[#3DCCDE] font-semibold">{data?.jobId?.title}</span> has been
          submitted by <span className="text-[#3DCCDE] font-semibold">{data?.recruiterAgencyId?.companyName}</span> to
          the hiring company
        </span>
      );
    case EnumActivityTracker.APPLICATION_VIEWED:
      return (
        <span>
          The hiring company has viewed your application submitted for the
          position of <span className="text-[#3DCCDE] font-semibold">{data.jobId.title}</span>
        </span>
      );
    case EnumActivityTracker.CANDIDATE_APPLIED:
      return (
        <span>
          You applied for the position titled{" "}
          <span className="text-[#3DCCDE] font-semibold">{data?.jobId?.title}</span> posted
          by <span className="text-[#3DCCDE] font-semibold">{data?.recruiterAgencyId?.companyName}</span>
        </span>
      );
    case EnumActivityTracker.PROFILE_UPDATED:
      return (
        <span className="text-xs gap-1 flex flex-wrap text-charcoal">
          <BoldText>You </BoldText>
          successfully updated your skills.
        </span>
      );
    case EnumActivityTracker.USER_REGISTERED:
      return (
        <span className="text-xs gap-1 flex flex-wrap text-charcoal">
          <BoldText>You </BoldText>
          registered successfully on COMTRACKED.
        </span>
      );
    default:
      return (
        <span className="text-xs gap-1 flex flex-wrap text-charcoal">
          <BoldText>ABC Recruitment </BoldText>
          sent you an offer from <BoldText>ABC Recruitment </BoldText>
          regarding
          <BoldText>ABC Recruitment.</BoldText>
        </span>
      );
  }
}

function getStatus(type: EnumActivityTracker) {
  switch (type) {
    case EnumActivityTracker.SUCCESSFUL_PLACEMENT:
      return 'Successful';
    case EnumActivityTracker.OFFER_RECEIVED:
      return 'Offer Received';
    case EnumActivityTracker.INTERVIEW_INVITE_SENT:
      return 'Interview Invitation';
    case EnumActivityTracker.MEETING_FORWARDED:
      return 'Availability Poll';
    case EnumActivityTracker.CANDIDATE_SHORTLISTED:
      return 'Shortlisted';
    case EnumActivityTracker.SHORTLIST_BY_RECRUITER_AGENCY:
      return 'Shortlisted';
    case EnumActivityTracker.APPLICATION_DECLINE_BY_RECRUITER_AGENCY:
      return 'Application Declined';
    case EnumActivityTracker.APPLICATION_SUBMITTED:
      return 'Application Submitted';
    case EnumActivityTracker.APPLICATION_VIEWED:
      return 'Application Viewed';
    case EnumActivityTracker.CANDIDATE_APPLIED:
      return 'Applied';
    case EnumActivityTracker.PROFILE_UPDATED:
      return 'Profile Edit';
    case EnumActivityTracker.USER_REGISTERED:
      return 'Registration';
    default:
      return type;
  }
}


type CardProps = {
  type: EnumActivityTracker;
  data: IActivityTracker;
};

const Card = ({ data }: CardProps) => {
  return (
    <div className="grid grid-cols-[0.45fr_1.55fr] gap-8 items-center">
      <div className="flex flex-col gap-1.5">
        <span className="text-xss font-semibold text-charcoal">
          {dayjs(data?.createdAt).format("hh:mm a")}
        </span>
        <span className="text-xss text-light-black">
          {dayjs(data?.createdAt).format("ddd Do")}{" "}
          {dayjs(data?.createdAt).format("MMMM YYYY")}
        </span>
        <div className="flex border-l-3 font-semibold text-charcoal text-xss border-l-[3px] border-l-[#6CABDD] border rounded-sm px-2 py-1">
          {getStatus(data?.eventType as EnumActivityTracker)}
        </div>
      </div>
      <div className=" flex flex-col justify-start text-xs font-medium text-charcoal border-b h-28 leading-6 pt-10">
        <span className="">{getDescription(data?.eventType as EnumActivityTracker, data)}</span>
      </div>
    </div>
  );
};

const LIMIT = 15;

const CandidateTrackerPage = () => {
  const sentinelRef = useRef(null);
  const scrollListRef = useRef(null);

  const [acitvities, setAcitvities] = useState([]);

  const [page, setPage] = useState(1)
  const [hasMoreData, setHasMoreData] = useState(false)
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false)

  // const fetchActivityTracker = async () => {
  // try {
  //   setIsLoading(true);
  //   const { data, success } = await getActivityTracker();
  //   if (success && data) {
  //     setAcitvities(data?.data);
  //   }
  // } catch (error) {
  //   console.error("Error: fetching activity trackers", error);
  // } finally {
  //   setIsLoading(false);
  // }
  // };

  const fetchActivityTracker = useCallback(async () => {
    try {
      if (page === 1) {
        setIsLoading(true);
      }
      const { data, success } = await getActivityTracker({
        limit: LIMIT,
        page
      });
      if (success && data) {
        setHasMoreData(data?.pagination?.totalPages > page)
        setAcitvities((prev) => page === 1 ? data?.data : [...prev, ...data?.data]);
      }
    } catch (error) {
      console.error("Error: fetching activity trackers", error);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false)
    }
  }, [page])

  const handleLoadMore = useCallback(async () => {
    if (isLoadingMore || isLoading || !hasMoreData) return
    setIsLoadingMore(true)
    setPage((prev) => prev + 1)
  }, [isLoading, isLoadingMore])


  useEffect(() => {
    fetchActivityTracker();
  }, [fetchActivityTracker]);

  useEffect(() => {
    if (!sentinelRef?.current || !scrollListRef?.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries?.[0]?.isIntersecting) {
          handleLoadMore();
        }
      },
      {
        root: scrollListRef?.current,
        rootMargin: "0px 0px 200px 0px",
        threshold: 0,
      }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [handleLoadMore]);

  return (
    <div className="flex flex-col flex-1 min-h-0 rounded-md bg-white pt-7">
      <span className="font-bold text-charcoal mx-auto">Activity Tracker</span>
      {/* Tracker List */}
      <div
        ref={scrollListRef}
        className="flex flex-col flex-1 min-h-0 mt-4 w-[70%] mx-auto overflow-y-scroll no-scrollbar">
        {isLoading ? (
          <LoadingAnimation color="black" className="w-9 h-9 m-auto" />
        ) : acitvities?.length ? (
          <>
            {
              acitvities?.map((activity) => {
                return <Card type={activity?.eventType} data={activity} />;
              })
            }
            {
              isLoadingMore && (
                <div className="flex items-center justify-center py-4 gap-2">
                  <LoadingAnimation color="black" className="w-5 h-5" />
                  <span className="font-medium text-xs italic text-light-black">Loading more data</span>
                </div>
              )
            }
            {/* Sentinel observer */}
            <div
              ref={sentinelRef}
              className="h-px shrink-0"
              aria-hidden
            />
          </>
        ) : (
          <div className="flex-col gap-3 m-auto h-[90vh] flex justify-center items-center font-medium text-light-black text-sm">
            <TrackIcon className={"w-16 h-16"} fill={"gray"} />
            No acitvities recorded till now.
          </div>
        )}
      </div>
    </div>
  );
};

export default CandidateTrackerPage;
